import { type PropsWithChildren, Fragment, useContext, useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, GridLegacy as Grid, useMediaQuery, useTheme } from "@mui/material";

import flex from "../../../../../styles/flex";

import TextField from "@mui/material/TextField";
import TabbedView from "../../../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../../../components/lists/TabbedView/TabbedViewPage";
import TextInputField from "../../../../../components/editable/EditableField/fields/TextInputField";
import { PagedDataView } from "../../../../../components/lists/PagedDataView/PagedDataView";
import DateField from "../../../../../components/editable/EditableField/fields/DateField";
import { EditableGroupContext } from "../../../../../components/editable/EditableGroup/EditableGroup";
import ErrorDialog from "../../../../../components/notifications/errorDialog/ErrorDialog";

import AirportResult from "./components/AirportResult";
import FlightResult from "./components/FlightResult";
import FlightResultConfirmDialog from "./components/FlightResultConfirmDialog";

import { api, IFlightDestinationLookup, IFlightDestination, IFlightLookup, IFlightLookupItem, Booking, Terminal, RequestStatus } from "../../../../../logic/api";

import { SessionContext } from "../../../../../utility/useSession";
import { useDate } from "../../../../../utility/useDate";
import { useDebounce } from "../../../../../utility/useDebounce";
import { usePreferences } from "../../../../../utility/usePreferences";
import { ReleaseFlag, useReleaseManagement } from "../../../../../utility/feature-management";
import TimeField from "../../../../../components/editable/EditableField/fields/TimeField";
import NumberSpinnerInputField from "../../../../../components/editable/EditableField/fields/NumberSpinnerInputField";
import PortalDatePicker from "../../../../../components/form/PortalDatePicker";
import { FiAlertTriangle } from "react-icons/fi";
import { useTranslation } from "react-i18next";

const FlightLookup = (props: PropsWithChildren<{
  isNewBooking: boolean,
}>) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const editableState = useContext(EditableGroupContext);
  const { localizeDate } = useDate();
  const { isFlightLookupEnabled } = usePreferences();
  const isHxAirportLookupReleased = useReleaseManagement(ReleaseFlag.hxAirportLookup);

  const homeIataCode = session.current.site?.home?.iata || "";

  const stateData = { ...editableState.data as Record<string, unknown> } as unknown as Booking;

  const isFlightDataPopulated = (): boolean => {
    if (!editableState?.data) {
      return false;
    }

    if (!props.isNewBooking) {
      return true;
    }

    const returnFlightNumber = stateData.returnFlight.flightNumber || "";
    return Boolean(returnFlightNumber.length);
  }

  const getReturnFlightDate = () => {
    return stateData.returnFlight.flightDate
      ?? stateData.bookingEndDate
      ?? stateData.bookingStartDate
      ?? new Date();
  }

  const bookingStartDate = stateData.bookingStartDate;
  const bookingEndDate = stateData.bookingEndDate;

  const isEditing = (): boolean => Boolean(editableState?.data && editableState.isEditing);

  const [enterManually, setEnterManually] = useState(!isFlightLookupEnabled ? true : isFlightDataPopulated());
  const [showFlightLookup, setShowFlightLookup] = useState(false);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const [searchByAirportCity, setSearchByAirportCity] = useState("");
  const debouncedAirportCity = useDebounce(searchByAirportCity, 500);
  const [selectedDestination, setSelectedDestination] = useState<IFlightDestination | null>(null);

  const [searchByFlightNo, setSearchByFlightNo] = useState("");
  const debouncedFlightNo = useDebounce(searchByFlightNo, 500);

  const [dateFilter, setDateFilter] = useState("");
  const [timeFilter, setTimeFilter] = useState("");
  const [timeWindowFilter, setTimeWindowFilter] = useState(0);

  const [searchByAirportCityParams, setSearchByAirportCityParams] = useState<IFlightDestinationLookup | null>(null);
  const [searchFlightParams, setSearchFlightParams] = useState<IFlightLookup | null>(null);
  const [searchByFlightNoParams, setSearchByFlightNoParams] = useState<IFlightLookup | null>(null);

  const [selectedFlight, setSelectedFlight] = useState<IFlightLookupItem | null>(null);
  const [showSelectedFlightConfirmation, setShowSelectedFlightConfirmation] = useState(false);
  const [terminalErr, setTerminalErr] = useState<Error | null>(null);

  const isLargeDevice = useMediaQuery(useTheme().breakpoints.up("md"));

  useEffect(() => {
    // On load
    if (showFlightLookup) {
      const data = { ...editableState.data as Record<string, unknown> } as unknown as Booking;

      const returnDate = localizeDate(getReturnFlightDate()).toISO() ?? "";
      const destination = selectedDestination?.name ||
        (releaseImproveFlightLookup
          ? (data?.returnFlight?.departureAirport || data?.outboundFlight?.flightDestination)
          : data?.outboundFlight?.flightDestination) || "";
      const flightNumber = data?.returnFlight?.flightNumber || "";
      const selectedTabIndex = flightNumber ? 1 : 0;
      const tolerance = Number(session.current.site?.preferences.flight_lookup_tolerance_hours);
      setSelectedTabIndex(selectedTabIndex);

      setSelectedDestination(null);
      setSearchByAirportCity(destination);

      setSearchByFlightNo(flightNumber);

      setDateFilter(returnDate);
      setTimeFilter(returnDate);
      setTimeWindowFilter(tolerance);
    }

    // On unload
    if (!showFlightLookup) {
      setSearchByAirportCity("");
      setSearchByFlightNo("");
      setSelectedDestination(null);
      setSearchByAirportCityParams(null);
      setSearchFlightParams(null);
      setSearchByFlightNoParams(null);
    }
  }, [showFlightLookup]);

  // Change airport search state from destination picked to start state
  useEffect(() => {
    if (selectedTabIndex != 0 || searchByAirportCity || !selectedDestination) {
      return;
    }

    setSelectedDestination(null);
    setSearchFlightParams(null);
    setSearchByAirportCityParams(null);
  }, [selectedTabIndex, searchByAirportCity]);

  // Perform airport search if city is available and no destination picked
  useEffect(() => {
    if (selectedTabIndex != 0 || !searchByAirportCity || selectedDestination) {
      return;
    }

    const params: IFlightDestinationLookup = { filter: debouncedAirportCity };
    if (isHxAirportLookupReleased) {
      params.date = getReturnFlightDate();
    }

    setSearchByAirportCityParams(params);
  }, [selectedTabIndex, debouncedAirportCity]);

  // Airport flight search
  useEffect(() => {
    if (selectedTabIndex != 0 || !selectedDestination) {
      return;
    }

    searchFlightsByIata(selectedDestination.iata);
  }, [selectedTabIndex, dateFilter, timeFilter, timeWindowFilter]);

  // Flight no search
  useEffect(() => {
    if (selectedTabIndex != 1) {
      return;
    }

    searchFlightsByFlightFilters(searchByFlightNo.toString().toUpperCase());
  }, [selectedTabIndex, debouncedFlightNo, dateFilter, timeFilter, timeWindowFilter]);

  const isDatePickerReleased = useReleaseManagement(ReleaseFlag.releaseBookingDatepicker);
  const releaseImproveFlightLookup = useReleaseManagement(ReleaseFlag.releaseImproveFlightLookup);

  const handleFlightNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchByFlightNo(e.target.value);
  }

  const closeDialog = () => {
    setShowFlightLookup(false);
    setEnterManually(true);
  };

  const selectAirport = (airport: IFlightDestination) => {
    setSearchByAirportCity(airport.name);
    setSelectedDestination(airport);
    searchFlightsByIata(airport.iata);
  };

  const handleFilters = (): { startDate: string, endDate: string } => {
    let startDate = new Date();
    let endDate = new Date();

    if (dateFilter) {
      startDate = new Date(dateFilter);
      endDate = new Date(dateFilter);
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    if (timeFilter) {
      const timeFilterAsDate = new Date(timeFilter);
      startDate.setHours(timeFilterAsDate.getHours(), timeFilterAsDate.getMinutes(), timeFilterAsDate.getSeconds(), timeFilterAsDate.getMilliseconds());
      endDate.setHours(timeFilterAsDate.getHours(), timeFilterAsDate.getMinutes(), timeFilterAsDate.getSeconds(), timeFilterAsDate.getMilliseconds());
    }

    if (timeWindowFilter) {
      startDate.setTime(startDate.getTime() - (timeWindowFilter * 60 * 60 * 1000));
      endDate.setTime(endDate.getTime() + (timeWindowFilter * 60 * 60 * 1000));
    }

    return {
      startDate: localizeDate(startDate).toISO() ?? "",
      endDate: localizeDate(endDate).toISO() ?? "",
    }
  }

  const searchFlightsByIata = (iata: string) => {
    if (!session.current.site) {
      return;
    }

    const filters = handleFilters();

    setSearchFlightParams({
      startDate: filters.startDate,
      endDate: filters.endDate,
      source: iata,
      isSourceIATA: true
    });
  };

  const searchFlightsByFlightFilters = (flightNo: string) => {
    if (!session.current.site) {
      return;
    }

    const filters = handleFilters();

    setSearchByFlightNoParams({
      startDate: filters.startDate,
      endDate: filters.endDate,
      flightNo: flightNo
    });
  };

  const selectFlight = (flight: IFlightLookupItem) => {
    setSelectedFlight(flight);
    setShowSelectedFlightConfirmation(true);
  };

  const confirmSelectedFlight = () => {
    if (!editableState?.data || !selectedFlight || !editableState.setData) {
      return;
    }

    setShowFlightLookup(false);
    setShowSelectedFlightConfirmation(false);

    const data = { ...editableState.data as Record<string, unknown> } as unknown as Booking;
    data.returnFlight.flightDate = localizeDate(new Date(selectedFlight.relativeDateTime)).toISO();
    data.returnFlight.flightNumber = `${selectedFlight.airline}${selectedFlight.flight_no}`;
    data.returnFlight.departureAirport = selectedFlight.city;
    data.returnFlight.departureAirportIATA = selectedFlight.transfer;


    const terminalCode = selectedFlight.terminal || "";

    if (session.current.site) {
      const terminal = session.current.site.terminals.find((term) => term.terminal.code === terminalCode);

      if (!terminalCode) {
        setTerminalErr(new Error(t("errors.noTerminal")));
      } else {
        if (terminal) {
          data.returnFlight.terminal = terminal.terminal;
        } else {
          data.returnFlight.terminal = {
            id: -1,
          } as Terminal;
          setTerminalErr(new Error(t("errors.noAccessTerminalCode", { terminalCode: terminalCode })));
        }
      }
    }

    editableState.setData(data);
    setEnterManually(true);
  };

  if (showFlightLookup && !homeIataCode) {
    return <ErrorDialog err={new Error(t("errors.noIATACode"))} onClose={() => {
      setShowFlightLookup(false);
      setEnterManually(true);
    }}></ErrorDialog>
  }

  return <Fragment>
    <ErrorDialog err={terminalErr}></ErrorDialog>

    <Dialog maxWidth={"lg"} keepMounted open={showFlightLookup && !showSelectedFlightConfirmation}>
      <DialogTitle>{t("flightLookup.labels.searchFlights")}</DialogTitle>

      <DialogContent style={{ minHeight: 120, textAlign: "center", overflowY: "unset" }}>
        <TabbedView animated={false} selectedIndex={selectedTabIndex} onSelectedIndexChange={setSelectedTabIndex}>
          <TabbedViewPage title={t("flightLookup.labels.cityAirport")}>
            <div style={{ padding: 10 }}>
              <Grid container justifyContent="space-evenly" spacing={1}>
                <Grid item style={{ flex: `${selectedDestination ? "0 0 auto" : "1 1 100%"}`, textAlign: "center" }}>
                  <TextInputField clearOnFocus={true} label={t("input.labels.cityOrAirport")} maxWidth={"100%"} value={searchByAirportCity} onChange={setSearchByAirportCity} />
                </Grid>

                {selectedDestination && (
                  <>
                    <Grid item style={{ ...flex.flexAuto, textAlign: "right" }}>
                      {isDatePickerReleased ? <PortalDatePicker
                        minDate={new Date(bookingStartDate)}
                        maxDate={new Date(bookingEndDate)}
                        label={t("input.labels.returningOn")}
                        value={dateFilter}
                        defaultMinDate
                        onChange={(val) => {
                          if (val) { setDateFilter(val?.toString()) }
                        }} /> : <Grid item style={{ ...flex.flexAuto, textAlign: "right" }}>
                        <DateField label={t("input.labels.returningOn")} value={dateFilter} onChange={setDateFilter} />
                      </Grid>}
                    </Grid>
                    <Grid item style={{ ...flex.flexAuto, textAlign: "right" }}>
                      <TimeField label={t("input.labels.returningAt")} value={timeFilter} onChange={(val) => setTimeFilter(val)} />
                    </Grid>
                    <Grid item style={{ ...flex.flexAuto, textAlign: "left" }}>
                      <NumberSpinnerInputField value={timeWindowFilter} label={t("input.labels.hours")} onChange={(val) => setTimeWindowFilter(val)} min={1} maxWidth={80} />
                    </Grid>
                  </>
                )}
              </Grid>
            </div>
          </TabbedViewPage>

          <TabbedViewPage title={t("flightLookup.labels.flightNumber")}>
            <div style={{ padding: 10 }}>
              <Grid container justifyContent="space-evenly" spacing={1}>
                <Grid item style={{ ...flex.flexAuto }}>
                  <TextField label={t("flightLookup.labels.enterFlightNo")} size="small" variant="outlined" inputProps={{ style: { textTransform: "uppercase" } }} value={searchByFlightNo} onChange={handleFlightNumberChange} />
                </Grid>
                {isDatePickerReleased ? <PortalDatePicker
                  minDate={new Date(bookingStartDate)}
                  maxDate={new Date(bookingEndDate)}
                  label={t("input.labels.returningOn")}
                  value={dateFilter}
                  onChange={(val) => {
                    if (val) { setDateFilter(val?.toString()) }
                  }} /> : <Grid item style={{ ...flex.flexAuto }}>
                  <DateField label={t("input.labels.returningOn")} value={dateFilter} onChange={(val) => setDateFilter(val)} />
                </Grid>}

                <Grid item style={{ ...flex.flexAuto }}>
                  <TimeField label={t("input.labels.returningAt")} value={timeFilter} onChange={(val) => setTimeFilter(val)} />
                </Grid>
                <Grid item style={{ ...flex.flexAuto, textAlign: "left" }}>
                  <NumberSpinnerInputField value={timeWindowFilter} label={t("input.labels.hours")} onChange={(val) => setTimeWindowFilter(val)} maxWidth={80} min={1} />
                </Grid>
              </Grid>
            </div>
          </TabbedViewPage>
        </TabbedView>
      </DialogContent>

      <DialogContent style={{ border: "solid 2px lightgray", padding: 0, borderRadius: 3, height: `${!isLargeDevice ? '200px' : '400px'}`, maxHeight: 400, marginLeft: 20, marginRight: 20, overflowY: "scroll", overflowX: "hidden", backgroundColor: "#eee" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ border: "solid 2px transparent", minWidth: 600 }}>
            {(selectedTabIndex == 0 && showFlightLookup && !selectedDestination && searchByAirportCityParams?.filter) && (
              <PagedDataView<IFlightDestinationLookup, IFlightDestination>
                apiHook={api.hooks.flight.lookupDestination}
                parameters={searchByAirportCityParams}
                animate={false}
                paged={false}
                renderItem={(item) => <AirportResult item={item} selectAirport={selectAirport} />}
              >
                {t("input.validations.noResults")}
              </PagedDataView>
            )}

            {(selectedTabIndex == 0 && showFlightLookup && selectedDestination && searchFlightParams) && (
              <FlightLookupDataView searchFlightParams={searchFlightParams} selectFlight={selectFlight} />
            )}

            {(selectedTabIndex == 1 && showFlightLookup && searchByFlightNoParams?.flightNo) && (
              <FlightLookupDataView searchFlightParams={searchByFlightNoParams} selectFlight={selectFlight} />
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => closeDialog()} color="primary">{t("general.cancel")}</Button>
      </DialogActions>
    </Dialog>

    <FlightResultConfirmDialog
      open={showSelectedFlightConfirmation}
      selectedFlight={selectedFlight}
      onCancel={() => setShowSelectedFlightConfirmation(false)}
      onConfirm={confirmSelectedFlight} />

    {
      enterManually
        ? (
          <Fragment>
            {isEditing() && isFlightLookupEnabled && (
              <div style={{ textAlign: "right", paddingRight: 10, paddingBottom: 10 }}>
                <Button variant="outlined" color="secondary" onClick={() => {
                  setShowFlightLookup(true);
                }}>
                  <span>{t("flightLookup.labels.searchFlights")}</span>
                </Button>
              </div>
            )}

            {props.children}
          </Fragment>
        )
        : (
          <div style={{ ...flex.flexRows, height: 140, alignItems: "center" }}>
            <div style={{ ...flex.flexFill, textAlign: "right", alignItems: "center" }}>
              <div style={{ padding: 10 }}>
                <Button variant="contained" color="secondary" onClick={() => {
                  setShowFlightLookup(true);
                }}>{t("flightLookup.labels.searchFlights")}</Button>
              </div>
            </div>
            <div style={{ ...flex.flexFill, textAlign: "left" }}>
              <div style={{ padding: 10 }}>
                <Button variant="outlined" color="secondary" onClick={() => {
                  setEnterManually(true);
                }}>{t("general.enterManually")}</Button>
              </div>

            </div>
          </div>
        )
    }
  </Fragment>
}

interface FlightLookupDataViewProps {
  searchFlightParams: IFlightLookup;
  selectFlight: (flight: IFlightLookupItem) => void;
}

const FlightLookupDataView = ({
  searchFlightParams,
  selectFlight,
}: FlightLookupDataViewProps) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const lookupFlight = api.hooks.flight.lookupFlight();

  useEffect(() => {
    if (!searchFlightParams) {
      return;
    }

    lookupFlight.send(session.current.context, searchFlightParams);
  }, [searchFlightParams]);

  if (lookupFlight.status === RequestStatus.loading) {
    return <div style={{ textAlign: "center" }}><CircularProgress /></div>;
  }

  if (lookupFlight.status === RequestStatus.error) {
    return <Fragment>
      <FiAlertTriangle color="red"></FiAlertTriangle>
    </Fragment>;
  }

  if (!lookupFlight.data?.length) {
    return <>{t("input.validations.noResults")}</>
  }

  return <>
    {lookupFlight.data.map((flight, i) => <FlightResult key={i} item={flight} selectFlight={selectFlight} />)}
  </>
}

export default FlightLookup;
